import { PayChannel } from "@features/api/memberApi";
import { Track } from "@/src/lib/tracking";

/**
 * 跳托管页前暂存渠道会话 id，回跳后取出做主动确认（EDG-552，fe-spec §3.4 ①③）。
 *
 * 为什么不拼进 return_url：return_url 在 `/payment/check` 拿到 session_id **之前**就已拼好；
 * Airwallex 是否在 success_url 追加 checkout id 未确认（fe-spec §7 R5）。
 *
 * sessionStorage 在无痕 / 配额满 / 禁用存储时会抛异常 —— 全部读写 try/catch，写失败只打日志，
 * 回跳侧拿不到暂存就走 `no_session`（延迟刷新会员态，webhook 兜底），不影响下单本身。
 */
export type CheckoutStashKind = "checkout" | "payment_method";

export interface CheckoutStash {
  kind: CheckoutStashKind;
  sessionId: string;
  /** checkout：产品 id；payment_method：plan id */
  planId: string;
  channel: PayChannel;
  createdAt: number;
}

/** 与 Airwallex checkout 会话 1 小时有效期一致；过期的会话确认也不会成功。 */
export const CHECKOUT_STASH_TTL_MS = 60 * 60 * 1000;

const STASH_KEY_PREFIX = "osp_checkout_stash";
const LOG_TAG = "checkoutSessionStash";

const stashKey = (kind: CheckoutStashKind, planId: string): string =>
  `${STASH_KEY_PREFIX}:${kind}:${planId}`;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

function parseStash(
  raw: string | null,
  kind: CheckoutStashKind,
  planId: string,
): CheckoutStash | null {
  if (!raw) {
    return null;
  }
  const value: unknown = JSON.parse(raw);
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const stash = value as Partial<CheckoutStash>;
  const isValid =
    stash.kind === kind &&
    stash.planId === planId &&
    isNonEmptyString(stash.sessionId) &&
    isNonEmptyString(stash.channel) &&
    typeof stash.createdAt === "number" &&
    Date.now() - stash.createdAt <= CHECKOUT_STASH_TTL_MS;
  return isValid ? (stash as CheckoutStash) : null;
}

export function saveCheckoutStash(stash: CheckoutStash): void {
  try {
    window.sessionStorage.setItem(
      stashKey(stash.kind, stash.planId),
      JSON.stringify(stash),
    );
  } catch (error) {
    Track.log("warn", LOG_TAG, "save failed", {
      kind: stash.kind,
      cause: String(error),
    });
  }
}

/** 读后即删（防止后退 / 重复进入页面时重复确认）；不存在、解析失败、过期、字段非法 → null。 */
export function takeCheckoutStash(
  kind: CheckoutStashKind,
  planId: string,
): CheckoutStash | null {
  try {
    const key = stashKey(kind, planId);
    const raw = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    return parseStash(raw, kind, planId);
  } catch (error) {
    Track.log("warn", LOG_TAG, "take failed", {
      kind,
      cause: String(error),
    });
    return null;
  }
}

/** 回跳 cancel 时清理。 */
export function clearCheckoutStash(
  kind: CheckoutStashKind,
  planId: string,
): void {
  try {
    window.sessionStorage.removeItem(stashKey(kind, planId));
  } catch (error) {
    Track.log("warn", LOG_TAG, "clear failed", {
      kind,
      cause: String(error),
    });
  }
}
